import type { NatsGroup, NatsRpcBackend } from "./NatsRpcBackend";
import type { AlmTool, AlmToolCallOpts, RpcBackend, UnknownRecord } from "./RpcBackend";

/**
 * Routes each call to the NATS or WAMP backend. The NATS backend is resolved
 * live per call via `getNats()` (null until the connection lands / after it
 * drops), so routing self-heals across reconnects. NATS is chosen only when a
 * live NATS backend exists AND the call's per-group release flag is on — each
 * registered adapter carries a group, checked as `nats-piadmin-<group>`; an
 * unregistered call has no group and stays on WAMP. A `useBackend` kwarg forces
 * a backend (`0` = NATS, `1` = WAMP) and is stripped before forwarding.
 */
export class SwitchingRpcBackend implements RpcBackend {
  constructor(
    private readonly getNats: () => NatsRpcBackend | null,
    private readonly wamp: RpcBackend,
    private readonly flagEnabled: (flag: string) => boolean,
  ) {}

  private route(
    submodule: string,
    method: string,
    kwargs?: UnknownRecord,
  ): { backend: RpcBackend; kwargs?: UnknownRecord } {
    const { useBackend, ...rest } = kwargs ?? {};
    const cleaned = Object.keys(rest).length ? rest : undefined;
    const nats = this.getNats();
    if (useBackend === 0) {
      return { backend: nats ?? this.wamp, kwargs: cleaned };
    }
    if (useBackend === 1) {
      return { backend: this.wamp, kwargs: cleaned };
    }
    const group = nats?.groupFor(submodule, method) ?? null;
    const useNats = nats !== null && group !== null && this.groupEnabled(group);
    return { backend: useNats && nats ? nats : this.wamp, kwargs: cleaned };
  }

  // A group routes NATS only when the master `nats-piadmin` flag AND its own
  // `nats-piadmin-<group>` flag are on. The master flag is the migration kill
  // switch: turning it off pulls every group back to WAMP at once, without
  // touching the per-group rollout flags. `alm-tools` additionally requires
  // `alm-common`: its jira/ado/rally calls share the connection subjects gated
  // by `alm-common`, so a tools-only rollout must not route them to NATS ahead
  // of the shared layer. `nats-only` methods have no WAMP counterpart, so they
  // always route to NATS irrespective of flags — the kill switch included.
  private groupEnabled(group: NatsGroup): boolean {
    if (group === "nats-only") {
      return true;
    }
    if (!this.flagEnabled("nats-piadmin")) {
      return false;
    }
    if (!this.flagEnabled(`nats-piadmin-${group}`)) {
      return false;
    }
    return group === "alm-tools" ? this.flagEnabled("nats-piadmin-alm-common") : true;
  }

  rootCall<T>(method: string, args?: unknown[], kwargs?: UnknownRecord): Promise<T> {
    const { backend, kwargs: k } = this.route("root", method, kwargs);
    return backend.rootCall<T>(method, args, k);
  }

  sessionCall<T>(
    method: string,
    args?: unknown[],
    kwargs?: UnknownRecord,
    sessionId?: string,
    opts?: AlmToolCallOpts,
  ): Promise<T> {
    const { backend, kwargs: k } = this.route("session", method, kwargs);
    return backend.sessionCall<T>(method, args, k, sessionId, opts);
  }

  almCall<T>(method: string, args?: unknown[], kwargs?: UnknownRecord): Promise<T> {
    const { backend, kwargs: k } = this.route("alm", method, kwargs);
    return backend.almCall<T>(method, args, k);
  }

  almToolCall<T>(
    tool: AlmTool,
    method: string,
    args?: unknown[],
    kwargs?: UnknownRecord,
    opts?: AlmToolCallOpts,
  ): Promise<T> {
    const { backend, kwargs: k } = this.route(`alm.${tool}`, method, kwargs);
    return backend.almToolCall<T>(tool, method, args, k, opts);
  }

  almSyncCall<T>(
    tool: AlmTool,
    method: string,
    args?: unknown[],
    kwargs?: UnknownRecord,
    sessionId?: string,
    opts?: AlmToolCallOpts,
  ): Promise<T> {
    const { backend, kwargs: k } = this.route(`almsync.${tool}`, method, kwargs);
    return backend.almSyncCall<T>(tool, method, args, k, sessionId, opts);
  }

  onboardingCall<T>(
    method: string,
    args?: unknown[],
    kwargs?: UnknownRecord,
    opts?: AlmToolCallOpts,
  ): Promise<T> {
    const { backend, kwargs: k } = this.route("onboarding", method, kwargs);
    return backend.onboardingCall<T>(method, args, k, opts);
  }

  canHandle(submodule: string, method: string): boolean {
    return (
      (this.getNats()?.canHandle(submodule, method) ?? false) ||
      this.wamp.canHandle(submodule, method)
    );
  }
}
